import {
  useMutation,
  useQueryClient,
  type MutationKey,
  type QueryKey,
} from '@tanstack/react-query';

import { useToast } from '@/components/ui/toast';
import type { AdminApiResult } from './api';
import { unwrap } from './unwrap';

/** What a screen says and does about a write: its toasts and its next step. */
export interface MutationFeedback<TData, TInput> {
  /** The confirmation toast, or how to word it from the answer. None when omitted. */
  okToast?: string | ((data: TData, input: TInput) => string);
  /** The danger toast on any failure, or how to word it from the error. None when omitted. */
  failToast?: string | ((error: Error) => string);
  /** What the screen does next once the API said yes — close a panel, leave the page. */
  onSuccess?: (data: TData, input: TInput) => void;
}

export interface AdminMutationOptions<TData, TInput> extends MutationFeedback<TData, TInput> {
  fn: (input: TInput) => Promise<AdminApiResult<TData>>;
  /** The caches the write makes stale, invalidated on success. */
  invalidate?: readonly QueryKey[];
  mutationKey?: MutationKey;
}

/**
 * A console write: the call, its toast, its invalidations, its next step.
 *
 * Written nine times before this, each copy checking `res.ok` in `onSuccess` by hand and
 * each forgetting something different — a failure toast, the shell's badges, an
 * `onError` for the request that never answered. Here the result goes through `unwrap`,
 * so a refused write is an error like any other: the danger toast, and nothing
 * invalidated or moved on from. On success the order is fixed — toast, invalidate, then
 * the caller's `onSuccess`.
 */
export function useAdminMutation<TData, TInput = void>({
  fn,
  okToast,
  failToast,
  onSuccess,
  invalidate = [],
  mutationKey,
}: AdminMutationOptions<TData, TInput>) {
  const { toast } = useToast();
  const client = useQueryClient();

  return useMutation({
    mutationKey,
    mutationFn: async (input: TInput) => unwrap(await fn(input)),
    onSuccess: (data, input) => {
      if (okToast !== undefined) {
        toast({ title: typeof okToast === 'function' ? okToast(data, input) : okToast });
      }
      for (const queryKey of invalidate) void client.invalidateQueries({ queryKey });
      onSuccess?.(data, input);
    },
    onError: (error) => {
      if (failToast === undefined) return;
      toast({
        title: typeof failToast === 'function' ? failToast(error) : failToast,
        tone: 'danger',
      });
    },
  });
}
